import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/platform/auth/forgot_password", { email });
      setDone(true);
    } catch (err) {
      // Treat 404 as success — don't reveal which emails exist.
      if (err instanceof ApiError && err.status === 404) {
        setDone(true);
      } else {
        setError(
          err instanceof ApiError ? err.message : "Couldn't send reset email.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-[420px] anim-fade-up">
      <div
        className="rounded-[var(--radius-modal)] p-8 shadow-xl-eon"
        style={{
          background: "rgba(11, 13, 18, 0.55)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(36px) saturate(180%)",
          WebkitBackdropFilter: "blur(36px) saturate(180%)",
        }}
      >
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-slate-200)]">
            Reset
          </p>
          <h1 className="mt-1 text-heading text-[var(--color-snow)] font-semibold">
            {done ? "Check your inbox" : "Reset your password"}
          </h1>
          <p className="mt-2 text-[14px] text-[var(--color-slate-200)]">
            {done
              ? "If an account exists for that email, we sent a reset link. It expires in 30 minutes."
              : "Enter the email you sign in with and we'll send you a reset link."}
          </p>
        </div>

        {!done && (
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <label className="block">
              <span className="block text-[12px] font-medium text-[var(--color-slate-200)] mb-1.5">
                Email
              </span>
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbrand.com"
              />
            </label>

            {error && (
              <p className="text-[13px] text-[var(--color-ember)]">{error}</p>
            )}

            <Button
              type="submit"
              disabled={submitting || !email}
              className="w-full"
              size="lg"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}

        <div className="mt-5 text-[13px]">
          <Link
            to="/signin"
            className="text-[var(--color-slate-200)] hover:text-[var(--color-snow)] transition-colors"
          >
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
